import React from 'react';

import { PasswordInput, useTranslation } from '@testsigma/atoms';

interface UsernamePasswordFieldsProps {
  /** The watch function from react-hook-form used to retrieve the latest value from the form state */
  watch: (field: string) => any;
  /** Function to update the form field identified by fieldId with the value fieldValue*/
  handleUpdateFormValue: (fieldId: string, fieldValue: any) => void;
}

/**
 * Renders the UI for the fields (password, security token) related to the Username-Password login method.
 * @param watch props.watch The watch function from react-hook-form used to retrieve the latest value from the form state
 * @param props.handleUpdateFormValue Function to update the form field identified by fieldId with the value fieldValue
 *   - **Parameters:**
 *     - `fieldId` (string): The identifier of the form field to be updated.
 *     - `fieldValue` (any): The new value to set for the specified form field.
 *
 *   - **Returns:**
 *     - `void`: This function does not return a value; it updates the form field with the new value.
 *
 * @returns UI for fields associated with the Username-Password login method.
 */
const UsernamePasswordFields: React.FC<UsernamePasswordFieldsProps> = (
  props: UsernamePasswordFieldsProps,
) => {
  const { handleUpdateFormValue, watch } = props;
  const { t } = useTranslation();

  return (
    <>
      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('password.label')}
        </div>
        <PasswordInput
          hasMask={true}
          isRequired={true}
          className='w-[320px] h-7'
          type='password'
          onChange={(value) => handleUpdateFormValue?.('password', value)}
          hideErrorMessage={true}
          value={watch('password')}
          showOutline={true}
          placeholder = {t('password.label')}
        />
      </div>

      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('security-token.label')}
        </div>
        <PasswordInput
          isRequired={true}
          className='w-[320px] h-7'
          type='password'
          hasMask={true}
          onChange={(value) => handleUpdateFormValue?.('securityToken', value)}
          hideErrorMessage={true}
          value={watch('securityToken')}
          showOutline={true}
          placeholder = {t('security-token.label')}
        />
      </div>
    </>
  );
};

export { UsernamePasswordFields };
